import React from 'react'
import BaseButton from '@/components/common/BaseButton'
import type { NarrativeOcgActionsProps } from '../../types'
import { getActionClassName } from '../../utils/classNames'
import CLOSE_ICON from '@public/assets/close-bold.svg'
import RESET_ICON from '@public/assets/reset-sm.svg'
import ACCEPT_ICON from '@public/assets/action/check-ocg.svg'

interface ActionContentProps {
  icon: string
  label: string
  loading: boolean
}

const getButtonLoader = (variant: 'dots' | 'ocg' = 'dots') => ({
  loaderClassName: 'w-4 justify-center overflow-visible',
  loaderSize: 'small' as const,
  variant,
})

function ActionContent({ icon, label, loading }: ActionContentProps) {
  return (
    <span className="inline-flex h-4 items-center justify-center gap-1.5 leading-4">
      {!loading && <img alt="" className="block size-4 shrink-0" src={icon} />}
      <span className="leading-4">{label}</span>
    </span>
  )
}

function NarrativeOcgDestopActions({
  suggestedNarrative,
  canAcceptValidation,
  canDiscard,
  isActionBusy,
  actionLoading,
  onAcceptValidation,
  onDiscardValidation,
  onRevertValidation,
  handleActionClick,
  showDecisionActions = true,
  showSuggestionActions = true,
  showAcceptAction = true,
}: NarrativeOcgActionsProps) {
  return (
    <div className="narrative-ocg-panel__actions flex flex-wrap items-center justify-end gap-2 max-[640px]:w-full max-[640px]:justify-start">
      {showSuggestionActions && onRevertValidation ? (
        <BaseButton
          className={getActionClassName(
            'secondary',
            actionLoading === 'revert',
            true
          )}
          disabled={isActionBusy}
          loading={actionLoading === 'revert'}
          loader={getButtonLoader()}
          onClick={event => handleActionClick(event, onRevertValidation)}
          type="button"
        >
          <ActionContent
            icon={RESET_ICON}
            label="Revert"
            loading={actionLoading === 'revert'}
          />
        </BaseButton>
      ) : null}
      {showDecisionActions && canDiscard ? (
        <BaseButton
          className={getActionClassName(
            'secondary',
            actionLoading === 'discard',
            true
          )}
          disabled={isActionBusy}
          loading={actionLoading === 'discard'}
          loader={getButtonLoader()}
          onClick={event => handleActionClick(event, onDiscardValidation)}
          type="button"
        >
          <ActionContent
            icon={CLOSE_ICON}
            label="Discard"
            loading={actionLoading === 'discard'}
          />
        </BaseButton>
      ) : null}
      {showDecisionActions && showAcceptAction ? (
        <BaseButton
          className={getActionClassName(
            'primary',
            actionLoading === 'accept',
            true
          )}
          disabled={!canAcceptValidation || isActionBusy}
          loading={actionLoading === 'accept'}
          // Accepting re-runs OCG validation: KMK validating animation.
          loader={getButtonLoader('ocg')}
          onClick={event =>
            handleActionClick(
              event,
              () =>
                canAcceptValidation &&
                onAcceptValidation?.(suggestedNarrative ?? '')
            )
          }
          type="button"
        >
          <ActionContent
            icon={ACCEPT_ICON}
            label="Accept"
            loading={actionLoading === 'accept'}
          />
        </BaseButton>
      ) : null}
    </div>
  )
}

export default NarrativeOcgDestopActions
